import { type FC, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { getPlayerChartContext } from './context';
import { InstanceIdProvider } from './InstanceIdProvider';
import type { PlayerChartConfig, PlayerChartContextType, PlayerChartSeriesData } from '../types';

const COLORS = [
	'#7cb5ec',
	'#434348',
	'#90ed7d',
	'#f7a35c',
	'#8085e9',
	'#f15c80',
	'#e4d354',
	'#2b908f',
	'#f45b5b',
	'#91e8e1',
];

interface PlayerChartProviderProps {
	children: ReactNode;
	config: PlayerChartConfig;
	isVisible?: boolean;

	instanceId?: string;
}

export const PlayerChartProvider: FC<PlayerChartProviderProps> = ({
	children,
	config,
	isVisible = true,
	instanceId = 'default',
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const prevIsVisible = useRef(isVisible);

	const PlayerChartContext = getPlayerChartContext(instanceId);

	const isLiveMode = config.liveMode ?? false;
	const externalCurrentTime = config.externalCurrentTime ?? null;

	useEffect(() => {
		if (isVisible && !prevIsVisible.current) {
			setIsPlaying(false);
			setCurrentIndex(0);
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		}
		prevIsVisible.current = isVisible;
	}, [isVisible]);

	const chartResults = config.chartData;

	const qualityMap = useMemo(() => {
		const map = new Map<string, Map<number, number | null>>();
		chartResults.forEach((result) => {
			const seriesQualityMap = new Map<number, number | null>();
			result.data.forEach((point) => {
				if (point.quality !== undefined) {
					seriesQualityMap.set(point.x, point.quality);
				}
			});
			map.set(result.info.name, seriesQualityMap);
		});
		return map;
	}, [chartResults]);

	const allUniqueTimestamps = useMemo(() => {
		return Array.from(
			new Set(chartResults.flatMap((result) => result.data.map((point) => point.x))),
		).sort((a, b) => a - b);
	}, [chartResults]);

	const hasValidDataAtTimestamp = useCallback(
		(timestamp: number): boolean => {
			return chartResults.some((series) =>
				series.data.some(
					(point) => point.x === timestamp && point.y !== null && point.y !== undefined,
				),
			);
		},
		[chartResults],
	);

	const { minTimestamp, maxTimestamp, validTimestamps, validDataTimestamps } = useMemo(() => {
		let min: number;
		let max: number;

		if (config.dateRange) {
			min =
				config.dateRange.startDate instanceof Date
					? config.dateRange.startDate.getTime()
					: config.dateRange.startDate;
			max =
				config.dateRange.endDate instanceof Date
					? config.dateRange.endDate.getTime()
					: config.dateRange.endDate;
		} else {
			min = allUniqueTimestamps.length > 0 ? allUniqueTimestamps[0] : 0;
			max =
				allUniqueTimestamps.length > 0 ? allUniqueTimestamps[allUniqueTimestamps.length - 1] : 0;
		}

		const uniqueTimestamps = allUniqueTimestamps.filter(
			(timestamp) => timestamp >= min && timestamp <= max,
		);

		const finalTimestamps = [...uniqueTimestamps];
		if (config.dateRange && uniqueTimestamps.length > 0) {
			const firstRealTimestamp = uniqueTimestamps[0];
			if (min < firstRealTimestamp) {
				finalTimestamps.unshift(min);
			}

			const lastRealTimestamp = uniqueTimestamps[uniqueTimestamps.length - 1];
			if (max > lastRealTimestamp) {
				finalTimestamps.push(max);
			}
		}

		const validDataTimestamps = finalTimestamps.filter((timestamp) =>
			hasValidDataAtTimestamp(timestamp),
		);

		const valid = [...validDataTimestamps];
		if (config.dateRange) {
			if (!valid.includes(min)) {
				valid.unshift(min);
			}

			if (!valid.includes(max) && max !== min) {
				valid.push(max);
			}

			valid.sort((a, b) => a - b);
		}

		return {
			minTimestamp: min,
			maxTimestamp: max,
			validTimestamps: valid,
			validDataTimestamps,
		};
	}, [allUniqueTimestamps, config.dateRange, hasValidDataAtTimestamp]);

	const totalTimestamps = validTimestamps.length;

	const getLatestTimestampFromSeries = useCallback(() => {
		if (chartResults.length === 0) return null;
		
		let latestTimestamp = 0;
		chartResults.forEach((result) => {
			result.data.forEach((point) => {
				if (point.x > latestTimestamp && point.y !== null && point.y !== undefined) {
					latestTimestamp = point.x;
				}
			});
		});
		
		return latestTimestamp > 0 ? latestTimestamp : null;
	}, [chartResults]);

	const currentTimestamp = useMemo(() => {
		if (isLiveMode) {
			const latestFromSeries = getLatestTimestampFromSeries();
			return latestFromSeries ?? externalCurrentTime;
		}
		return validTimestamps[currentIndex] || null;
	}, [isLiveMode, getLatestTimestampFromSeries, externalCurrentTime, validTimestamps, currentIndex]);

	const baseSeries = useMemo(() => {
		return chartResults.map((result, index) => {
			const dataMap = new Map<number, number | null>();
			result.data.forEach((point) => {
				if (point.x >= minTimestamp && point.x <= maxTimestamp) {
					dataMap.set(point.x, point.y);
				}
			});

			return {
				name: result.info.name,
				dataMap,
				type: 'line' as const,
				color: COLORS[index % COLORS.length],
				zIndex: 2,
				connectNulls: true,
				valueUnit: result.info.valueUnit,
				additionalInfo: result.info.additionalInfo,
			};
		});
	}, [chartResults, minTimestamp, maxTimestamp]);

	const historicalSeries = useMemo(() => {
		if (!config.historicalData) return [];

		return config.historicalData.map((historicalConfig) => {
			const mainDataIndex = chartResults.findIndex(
				(result) => result.paramId === historicalConfig.paramId,
			);
			const seriesColor = mainDataIndex >= 0 ? COLORS[mainDataIndex % COLORS.length] : '#cccccc';

			const historicalDataMap = new Map<number, number | null>();
			historicalConfig.data.data.forEach((point) => {
				if (point.x >= minTimestamp && point.x <= maxTimestamp) {
					historicalDataMap.set(point.x, point.y);
				}
			});

			return {
				name: `${historicalConfig.data.info.name} (Historical)`,
				dataMap: historicalDataMap,
				type: 'line' as const,
				dashStyle: 'Dash' as const,
				color: seriesColor,
				zIndex: 1,
				connectNulls: true,
				valueUnit: historicalConfig.data.info.valueUnit,
				additionalInfo: historicalConfig.data.info.additionalInfo,
			};
		});
	}, [config.historicalData, chartResults, minTimestamp, maxTimestamp]);

	const series: PlayerChartSeriesData[] = useMemo(() => {
		if (currentTimestamp === null) return [];

		const result: PlayerChartSeriesData[] = [];

		for (const baseSerie of baseSeries) {
			const seriesData: [number, number | null][] = [];
			for (const timestamp of allUniqueTimestamps) {
				if (
					timestamp <= currentTimestamp &&
					timestamp >= minTimestamp &&
					timestamp <= maxTimestamp
				) {
					seriesData.push([timestamp, baseSerie.dataMap.get(timestamp) ?? null]);
				}
			}

			result.push({
				name: baseSerie.name,
				data: seriesData,
				type: baseSerie.type,
				dashStyle: 'Solid',
				color: baseSerie.color,
				zIndex: baseSerie.zIndex,
				connectNulls: baseSerie.connectNulls,
				valueUnit: baseSerie.valueUnit,
				additionalInfo: baseSerie.additionalInfo,
			});
		}

		for (const historicalSerie of historicalSeries) {
			const historicalSeriesData: [number, number | null][] = [];
			for (const timestamp of allUniqueTimestamps) {
				if (
					timestamp <= currentTimestamp &&
					timestamp >= minTimestamp &&
					timestamp <= maxTimestamp
				) {
					historicalSeriesData.push([timestamp, historicalSerie.dataMap.get(timestamp) ?? null]);
				}
			}

			result.push({
				name: historicalSerie.name,
				data: historicalSeriesData,
				type: historicalSerie.type,
				dashStyle: historicalSerie.dashStyle,
				color: historicalSerie.color,
				zIndex: historicalSerie.zIndex,
				connectNulls: historicalSerie.connectNulls,
				valueUnit: historicalSerie.valueUnit,
				additionalInfo: historicalSerie.additionalInfo,
			});
		}

		return result;
	}, [
		currentTimestamp,
		baseSeries,
		historicalSeries,
		allUniqueTimestamps,
		minTimestamp,
		maxTimestamp,
	]);

	useEffect(() => {
		if (isLiveMode) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		if (isPlaying) {
			if (currentIndex < totalTimestamps - 1) {
				intervalRef.current = setInterval(() => {
					setCurrentIndex((prev) => {
						const nextIndex = prev + 1;
						if (nextIndex >= totalTimestamps - 1) {
							setTimeout(() => setIsPlaying(false), 0);
							return totalTimestamps - 1;
						}
						return nextIndex;
					});
				}, config.playerSpeed ?? 1000);
			} else {
				setIsPlaying(false);
			}
		} else {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isPlaying, currentIndex, totalTimestamps, config.playerSpeed, isLiveMode]);

	const handlePlay = useCallback(() => {
		if (currentIndex >= totalTimestamps - 1) {
			setCurrentIndex(0);
		}
		setIsPlaying(true);
	}, [currentIndex, totalTimestamps]);

	const handleStop = useCallback(() => {
		setIsPlaying(false);
	}, []);

	const handleReset = useCallback(() => {
		setIsPlaying(false);
		setCurrentIndex(0);
	}, []);

	const contextValue: PlayerChartContextType = {
		isPlaying,
		currentIndex,
		totalTimestamps,
		series,
		currentTimestamp,
		minTimestamp,
		maxTimestamp,
		uniqueTimestamps: validTimestamps,
		originalTimestamps: validDataTimestamps,
		config,
		qualityMap,
		requestEvents: config.requestEvents ?? [],
		handlePlay,
		handleStop,
		handleReset,
		setCurrentIndex,
		isLiveMode,
	};

	return (
		<InstanceIdProvider instanceId={instanceId}>
			<PlayerChartContext.Provider value={contextValue}>{children}</PlayerChartContext.Provider>
		</InstanceIdProvider>
	);
};
